import { splitByRooms } from "../splitByRooms";
import { splitIntoRows } from "../splitCircuit";
import type { PlanRoomCircuits, RoomCircuitGroup } from "./types";

export const planRoomCircuits: PlanRoomCircuits = ({
  devices,
  keptCircuitIds,
  rooms,
  doors,
  maxPerGroup,
  boardPosition,
  pointsPerMetre,
}) => {
  const movable = devices.filter((device) => !device.circuitId || !keptCircuitIds.has(device.circuitId));
  if (movable.length === 0) return null;

  const asSplit = movable.map((device) => ({ id: device.key, position: device.position, isControl: device.isControl }));
  const byRooms = splitByRooms({ devices: asSplit, rooms, doors, maxPerGroup, boardPosition, pointsPerMetre });
  const method = byRooms ? "rooms" : "rows";
  const split =
    byRooms ??
    splitIntoRows({ devices: asSplit, maxPerGroup }).map((deviceIds) => ({ deviceIds, roomIds: [], roomNames: [] }));

  // Each group keeps the circuit that already holds most of it, biggest groups first, so ids and names stay put.
  const circuitOf = new Map(movable.map((device) => [device.key, device.circuitId]));
  const regroupable = new Set(movable.flatMap((device) => (device.circuitId ? [device.circuitId] : [])));
  const taken = new Set<string>();
  const circuitByGroup = new Map<number, string>();
  split
    .map((group, index) => ({ group, index }))
    .sort((a, b) => b.group.deviceIds.length - a.group.deviceIds.length || a.index - b.index)
    .forEach(({ group, index }) => {
      const counts = new Map<string, number>();
      group.deviceIds.forEach((key) => {
        const circuitId = circuitOf.get(key);
        if (circuitId && !taken.has(circuitId)) counts.set(circuitId, (counts.get(circuitId) ?? 0) + 1);
      });
      const best = [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0];
      if (!best) return;
      taken.add(best[0]);
      circuitByGroup.set(index, best[0]);
    });

  const groups: RoomCircuitGroup[] = split.map((group, index) => ({
    deviceKeys: group.deviceIds,
    roomIds: group.roomIds,
    roomNames: group.roomNames,
    circuitId: circuitByGroup.get(index) ?? null,
  }));
  return {
    method,
    groups,
    keptCount: devices.length - movable.length,
    emptiedCircuitIds: [...regroupable].filter((circuitId) => !taken.has(circuitId)).sort(),
  };
};
